import React, { useState, useEffect } from 'react';
import { NumberWithStaticUnitsInput, Tooltip } from 'components/common';

// A number and its unit entered in ONE row, for the case where the unit is a short fixed list rather
// than a value the user types: the count and the unit that count is measured in.
//
// The units are whatever `unit_values` lists. Nothing here is specific to any of them:
//
//   form_type: delayed_integer_with_static_units
//   unit_name: wastage_unit                              # sibling attribute the choice is written to
//   unit_values: [sheet, product]                        # ANY values -- [mm, in] works the same way
//   unit_label: quote_builder.wastage_unit               # optional, names the picker for screen readers
//   unit_translate_prefix: 'quote_builder.wastage_unit_' # optional; absent means the values ARE the labels
//   unit_publish_item_key: quote_builder.…_changed       # optional, published when the unit changes
//
// The unit itself lives in the sibling attribute named by `unit_name`, which is what the form posts;
// this field only decides where it is entered. `delayed_integer_with_unit` is untouched and still
// renders the unit as a read-only label taken from another attribute.
const DelayedNumberWithStaticUnitsFormField = ({ config, value, values, setValue, register, errors, readOnly, disabled, integerOnly, requestPublishItemOnChange }) => {
  const [inputValue, setInputValue] = useState(value);
  const unit = values[config.unit_name];

  const handleDelayedInputValue = (value) => {
    setValue(config.name, value);
    if(config.publish_on_change_key != undefined) {
      var event = new CustomEvent(config.publish_on_change_key, { "detail": value });
      document.dispatchEvent(event);
    }

    if((config.publish_item_on_change && config.publish_item_if_eval == undefined) || (config.publish_item_on_change && eval(config.publish_item_if_eval)) ) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }
  }

  useEffect(() => {
    const timeOutId = setTimeout(() => handleDelayedInputValue(inputValue), 500);
    return () => clearTimeout(timeOutId);
  }, [inputValue]);

  // No naming convention is assumed: with a prefix the label is looked up, without one the value is
  // shown as it stands, so a list of already-readable units needs no translation keys at all.
  const unitLabel = (unitValue) => {
    return config.unit_translate_prefix != undefined ? t(config.unit_translate_prefix + unitValue) : unitValue;
  }

  const unitOptions = config.unit_values.map((unitValue) => {
    return { value: unitValue, label: unitLabel(unitValue) };
  });

  const handleUnitChange = (event) => {
    setValue(config.unit_name, event.target.value);
    // The form posts when updated_at changes, so choosing a unit has to touch it the way a select does.
    setValue('updated_at', Date.now());
  }

  // Queued on mount as well as on change, the way SelectFormField publishes, so subscribers still
  // receive the unit with the form's first post.
  useEffect(() => {
    if(config.unit_publish_item_key != undefined) {
      requestPublishItemOnChange({ publish_item_key: config.unit_publish_item_key });
    }
  }, [unit]);

  return (
    <tr>
      <td>
        {t(config.label)} { config.tooltip_text != undefined && <Tooltip title={t(config.tooltip_text)} /> }
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">
        <NumberWithStaticUnitsInput
          name={config.name}
          placeholder={config.placeholder}
          register={register}
          errors={errors}
          class_name={config.class_name}
          integerOnly={integerOnly}
          unitName={config.unit_name}
          unitValue={unit}
          unitOptions={unitOptions}
          unitAriaLabel={config.unit_label != undefined ? t(config.unit_label) : undefined}
          onUnitChange={handleUnitChange}
          unitDisabled={readOnly || disabled}
          validators={config.validators != undefined ? config.validators : []}
          value={inputValue}
          onChange={event => setInputValue(event.target.value)}
          readOnly={readOnly}
        />
      </td>
    </tr>
  )
}

export default DelayedNumberWithStaticUnitsFormField;
